/**
 * CG-7 / F-201 — persistence for the onboarding step-0 role fork.
 *
 * The fork choice ("I'm the athlete / parent / coach") is stored locally so a
 * refresh doesn't re-ask. F-201: the original blob was a bare, UN-NAMESPACED
 * string, so a PRIOR account's `athlete` choice in a shared browser skipped the
 * role picker for a brand-new signup (a fresh coach/guardian landed straight in
 * the athlete wizard). It is now stamped with the owning auth user id — mirror
 * of the wizard blob's per-user keying (ff62c01) — so a foreign or legacy
 * (unstamped) value reads as ABSENT and the picker is shown.
 *
 * Plain module (no React / no CSS) so the auth layer can purge it on
 * auth-state change without pulling in onboarding component code.
 */

export type ForkRole = 'athlete' | 'guardian' | 'coach';

export const FORK_STORAGE_KEY = 'wai:onboarding:fork:v1';

type ForkBlob = { userId: string; role: ForkRole };

function readBlob(): Partial<ForkBlob> | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(FORK_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Partial<ForkBlob>;
  } catch {
    // Legacy bare-string value ('athlete') — unstamped → treated as absent.
    return null;
  }
}

/** The persisted fork choice, but ONLY when the stored blob is stamped with —
 *  and matches — `userId`. A foreign, unstamped (legacy), or missing-id blob is
 *  treated as ABSENT (returns null) so the step-0 role picker is shown. */
export function readForkChoice(userId: string | null | undefined): ForkRole | null {
  if (!userId) return null;
  const blob = readBlob();
  if (!blob || blob.userId !== userId) return null;
  return blob.role === 'athlete' || blob.role === 'guardian' || blob.role === 'coach'
    ? blob.role
    : null;
}

/** Persist the fork choice stamped with the owning auth user id. Best-effort —
 *  a blocked localStorage just re-asks next time. No-op without a user id. */
export function writeForkChoice(role: ForkRole, userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.setItem(FORK_STORAGE_KEY, JSON.stringify({ userId, role } satisfies ForkBlob));
  } catch {
    /* best-effort */
  }
}

/** B454 — forget THIS user's own fork choice (Back from the name screen to
 *  "Who is this account for?"), so a reload on that screen asks again instead
 *  of skipping past it. A blob owned by anyone else is left alone: purging
 *  that is `purgeForeignForkChoice`'s job. No-op without a user id. */
export function clearForkChoice(userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  const blob = readBlob();
  if (!blob || blob.userId !== userId) return;
  try {
    window.localStorage.removeItem(FORK_STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}

/** Remove the fork blob if it is NOT owned by `userId` (foreign OR unstamped
 *  legacy). Called on auth-state change so a prior account's fork choice can't
 *  linger in a shared browser. A blob owned by the current user is preserved. */
export function purgeForeignForkChoice(userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(FORK_STORAGE_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  const blob = readBlob();
  // Own it? keep. Foreign or legacy-unstamped (readBlob null on a bare string
  // while raw is non-empty)? purge.
  if (blob && blob.userId === userId) return;
  try {
    window.localStorage.removeItem(FORK_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
